import { useCallback } from 'react';

import { makeStyles } from 'tss-react/mui';

/** Every picker chip centers its leading glyph in a box this size, so all the chips align. */
export const PICKER_CHIP_ICON_SIZE = 16;

/**
 * Shared styling for inline picker chips — clickable values that open a popover
 * (priority, board, status, automation destination). They read as plain text until
 * you reach for them, then reveal a hairline outline on hover and while open. The
 * border is always present but transparent, so nothing reflows on hover.
 */
const usePickerChipMakeStyles = makeStyles()(theme => ({
  chip: {
    '&:hover': {
      borderColor: theme.appColors.rule,
    },
    border: '1px solid transparent',
    borderRadius: 8,
    cursor: 'pointer',
    maxWidth: '100%',
    padding: theme.spacing(0.25, 1),
    transition: 'border-color 0.2s ease-in-out',
    width: 'fit-content',
  },
  chipOpen: {
    borderColor: theme.appColors.rule,
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  icon: {
    /** Doubled selector so it outranks the border/box rules of icons like ProgressiveColumnIcon. */
    '&&': {
      border: 'none',
      borderRadius: 0,
      height: PICKER_CHIP_ICON_SIZE,
      minWidth: PICKER_CHIP_ICON_SIZE,
      width: PICKER_CHIP_ICON_SIZE,
    },
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
  },
}));

export function usePickerChipStyles(): {
  /** Pass the popover's open state, plus any call-site class to merge in. */
  getPickerChipClassName: (isOpen?: boolean, className?: string) => string;
  /** For laying out icon + label inside a trigger the chip class isn't applied to. */
  pickerChipContentClassName: string;
  pickerChipIconClassName: string;
  pickerChipLabelClassName: string;
} {
  const { classes, cx } = usePickerChipMakeStyles();
  const getPickerChipClassName = useCallback(
    (isOpen?: boolean, className?: string) =>
      cx(classes.chip, classes.content, isOpen && classes.chipOpen, className),
    [classes, cx]
  );

  return {
    getPickerChipClassName,
    pickerChipContentClassName: classes.content,
    pickerChipIconClassName: classes.icon,
    pickerChipLabelClassName: classes.label,
  };
}
